<script>
import { mapActions, mapGetters } from 'vuex'
import get from 'lodash/get'
import { formatDate } from '@lib/utils/format-date'
import OfferMixin from './offer.mixin'

export default {
  name: 'Offer',
  page: {
    title: 'Offer',
  },
  mixins: [OfferMixin],
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      offer: {},
      backRoute: { name: 'offers' },
    }
  },
  computed: {
    ...mapGetters('me', [
      'canCreateOffer',
      'canUpdateOffer',
    ]),
    isStatusActive() {
      return this.offer.active
    },
    isCouponCodeVisible() {
      return this.isOfferTypeCoupon(this.offer.type)
    },
    isCouponImageVisible() {
      return this.isOfferTypeShow(this.offer.type)
    },
    isCouponUrlVisible() {
      const couponType = this.offer.couponType
      const urlCouponType = this.isCouponTypeOnlineOnly(couponType) || this.isCouponTypeOnlineStore(couponType)
      return this.isOfferTypeUrl(this.offer.type) || (this.isOfferTypeCoupon(this.offer.type) && urlCouponType)
    },
    retailerName() {
      return get(this.offer, 'retailer.name')
    },
    offersInGroups() {
      return this.offer.offerGroups && this.offer.offerGroups.length ? this.offer.offerGroups.map(group => group.name).join(', ') : '—'
    },
    getActiveColor() {
      switch (this.offer.status) {
        case 'expired': return 'gray'
        case 'inactive': return 'red'
        default: return 'green'
      }
    },
  },
  created() {
    this.fetchOffer()
  },
  methods: {
    ...mapActions('offer', ['getOffer']),
    async fetchOffer() {
      this.loading = true
      const [err, response] = await this.getOffer(this.id)

      if (err) {
        this.$router.replace(this.backRoute)
      } else {
        this.offer = response
      }

      this.loading = false
    },
    formatDate(value, format) {
      return formatDate(value, format || 'DD/MM/YYYY')
    },
    onEdit() {
      const retailerId = get(this.offer, 'retailer.id')
      this.$router.push({
        name: 'retailer-offers',
        params: { id: retailerId },
        query: { active: this.offer.id },
      })
    },
  },
}
</script>

<template>
  <main-layout
    :title="retailerName"
    :back="backRoute"
    :loading="loading"
  >
    <data-box
      :status="false"
      header="Offer Details"
    >
      <template #buttons>
        <el-button
          v-if="canCreateOffer || canUpdateOffer"
          :class="$style.editBtn"
          type="primary"
          circle
          @click="onEdit"
        >
          <img
            :class="$style.editBtnIcon"
            src="../../assets/pen.svg"
          >
        </el-button>
      </template>

      <div>
        <data-list :class="!isStatusActive && $style.inactive">
          <dt>Status</dt>
          <dd :class="[$style.status, $style[getActiveColor]]">
            {{ offer.status }}
          </dd>

          <dt>Offer Name</dt>
          <dd>{{ offer.name || '—' }}</dd>

          <dt>Offer Description</dt>
          <dd v-html="offer.description || '—'" />

          <dt>Offer Type</dt>
          <dd>
            {{ offerTypeName(offer.type) || '—' }}
          </dd>

          <dt>Offer T&C's</dt>
          <dd v-html="offer.terms || '—'" />

          <dt>Offer Created</dt>
          <dd>
            {{ formatDate(offer.createdAt, 'DD/MM/YYYY') || '—' }}
          </dd>

          <dt>Offer End Date</dt>
          <dd>
            {{ offer.endsAt ? formatDate(offer.endsAt, 'DD/MM/YYYY') : '—' }}
          </dd>

          <template v-if="isCouponCodeVisible">
            <dt>Coupon Code</dt>
            <dd>
              {{ offer.couponCode || '—' }}
            </dd>
          </template>

          <template v-if="isCouponImageVisible">
            <dt>Redemption Image</dt>
            <dd>
              {{ offer.redemptionImage || '—' }}
            </dd>
          </template>

          <template v-if="offerHasVideo">
            <dt>Video</dt>
            <dd>
              <div
                v-if="offerHasVideo"
                :class="$style.video"
              >
                <video
                  v-if="!isYoutubeVideo"
                  width="300"
                  height="200"
                  preload="auto"
                  controls
                >
                  <source
                    :src="videoLink"
                    type="video/mp4"
                    codecs="avc1.42E01E, mp4a.40.2"
                  >
                </video>
                <iframe
                  v-else
                  width="300"
                  height="200"
                  :src="videoLink"
                  frameBorder="0"
                  allowFullScreen
                  title="Stream"
                />
              </div>
            </dd>
          </template>

          <template v-if="isCouponUrlVisible">
            <dt>URL</dt>
            <dd>
              {{ offer.promotionUrl || '—' }}
            </dd>
          </template>

          <dt>Participating Locations</dt>
          <dd>
            <ul v-if="offer.locations && offer.locations.length">
              <li
                v-for="(location, index) in offer.locations"
                :key="index"
              >
                {{ location.name }}
              </li>
            </ul>
            <template v-else>
              —
            </template>
          </dd>
          <dt>Offer in Groups</dt>
          <dd>
            {{ offersInGroups }}
          </dd>
        </data-list>
      </div>
    </data-box>
  </main-layout>
</template>

<style lang="scss" module>
.inactive {
  opacity: 0.4;
}

.editBtn {
  display: flex;
  align-items: center;
  justify-content: center;
}

.editBtnIcon {
  width: rem(14px);
  height: rem(14px);
}

.video {
  pointer-events: auto;
  display: flex;
  margin-bottom: 3rem;
  & video,
    iframe {
    border-radius: 10px;
    background-color: gray;
  }
}
.status {
  &::first-letter {
    text-transform: uppercase;
  }
}

.red {
  color: red;
}

.green {
  color: #0fbd1c;
}

.gray {
  color: gray;
}
</style>
